// Noise levels in the portal (user, 8 Oct 2026). The engine (cetalingua_engine/noise.py) measures every file; the
// pipeline writes one read-only summary per file: noiseLevels/{fileId} (firestore_doc in noise.py). Here: grouping
// per station by file / hour of day / day / week / month / season / year, and the CSV export. Values can never be
// edited in the portal.
//
// Grouping: Leq of a period = energy mean of the files' Leq weighted by their seconds (exact for Leq). Percentiles
// cannot be combined exactly from file summaries, so a period shows the MEDIAN of its files' L50 and the lowest L95 /
// highest L5 of its files (marked as such). Calibrated (dB re 1 uPa) and uncalibrated (dB re full scale) files are
// never combined; uncalibrated files are only compared within one station.

export const PERIODS = [['file', 'Each file'], ['hour', 'Hour of day'], ['day', 'Day'], ['week', 'Week'], ['month', 'Month'], ['season', 'Season'], ['year', 'Year']];
export const VERSIONS = [['ambient', 'Ambient (no animal sounds, no equipment noise)'], ['ambientWithEquipment', 'Ambient incl. equipment noise'], ['all', 'All sound']];

const toDate = (v) => (v && v.toDate ? v.toDate() : new Date(v));
export const recDate = (d) => toDate(d.recordedAt);

function isoWeek(dt) {
  const d = new Date(Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate()));
  const day = d.getUTCDay() || 7; d.setUTCDate(d.getUTCDate() + 4 - day);
  const y0 = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return [d.getUTCFullYear(), Math.ceil(((d - y0) / 86400000 + 1) / 7)];
}

/** meteorological seasons (Dec-Feb, Mar-May, Jun-Aug, Sep-Nov), swapped in the southern hemisphere; December
 *  belongs to the winter (northern) / summer (southern) of the next year: "Winter 2025/26". */
export function season(dt, lat) {
  const m = dt.getUTCMonth(); const y = dt.getUTCFullYear();
  const q = m === 11 || m <= 1 ? 0 : (m <= 4 ? 1 : (m <= 7 ? 2 : 3));
  const south = Number(lat) < 0;
  const names = south ? ['Summer', 'Autumn', 'Winter', 'Spring'] : ['Winter', 'Spring', 'Summer', 'Autumn'];
  const y0 = m === 11 ? y : (q === 0 ? y - 1 : y);
  return { key: `${q === 0 ? y0 : y}-${q === 0 ? 0 : q}`, label: q === 0 ? `${names[0]} ${y0}/${String(y0 + 1).slice(2)}` : `${names[q]} ${y}`, sort: q === 0 ? `${y0}-4` : `${y}-${q}` };
}

export function periodOf(d, period, lat) {
  const dt = recDate(d);
  const p2 = (n) => String(n).padStart(2, '0');
  if (period === 'file') return { key: d.id || d.fileId, label: `${dt.toISOString().slice(0, 16).replace('T', ' ')} · ${d.file || ''}`, sort: dt.toISOString() };
  if (period === 'hour') { const h = dt.getUTCHours(); return { key: `h${p2(h)}`, label: `${p2(h)}:00`, sort: p2(h) }; }
  if (period === 'day') { const s = dt.toISOString().slice(0, 10); return { key: s, label: s, sort: s }; }
  if (period === 'week') { const [y, w] = isoWeek(dt); return { key: `${y}-W${p2(w)}`, label: `${y} week ${w}`, sort: `${y}-${p2(w)}` }; }
  if (period === 'month') { const s = dt.toISOString().slice(0, 7); return { key: s, label: s, sort: s }; }
  if (period === 'season') return season(dt, lat);
  return { key: String(dt.getUTCFullYear()), label: String(dt.getUTCFullYear()), sort: String(dt.getUTCFullYear()) };
}

const emean = (vals, w) => {
  let s = 0; let n = 0;
  vals.forEach((v, i) => { if (Number.isFinite(v) && w[i] > 0) { s += w[i] * 10 ** (v / 10); n += w[i]; } });
  return n ? 10 * Math.log10(s / n) : null;
};
const median = (a) => { const v = a.filter(Number.isFinite).sort((x, y) => x - y); if (!v.length) return null; const m = Math.floor(v.length / 2); return v.length % 2 ? v[m] : (v[m - 1] + v[m]) / 2; };

/** every band of the files (by nominal frequency, low to high) — files of different sample rates have different
 *  band lists (13 Oct 2026: values were combined by POSITION, so a 16 kHz file's broadband was averaged with a
 *  48 kHz file's band and the chart line left the axes) */
export function bandUnion(docs) {
  const m = new Map();
  docs.forEach((d) => (d.bands || []).forEach((b) => { if (!m.has(b.nominal)) m.set(b.nominal, b); }));
  return [...m.values()].sort((a, b) => a.nominal - b.nominal);
}

/** docs of ONE station and ONE calibration state -> periods [{key, label, files, seconds, leq[], l50[], l95[], l5[]}]
 *  arrays have one value per band of bandUnion(docs) + broadband last; a file without a band gives no value there. */
export function aggregate(docs, period, version, lat) {
  const groups = new Map();
  docs.forEach((d) => {
    const s = d[version]; if (!s) return;
    const p = periodOf(d, period, d.site?.lat ?? lat);
    if (!groups.has(p.key)) groups.set(p.key, { ...p, docs: [] });
    groups.get(p.key).docs.push(d);
  });
  const union = bandUnion(docs);
  const nb = docs.length ? union.length + 1 : 0;
  // index of each union band in every file's own list (-1 = the file has no such band); broadband = the file's last
  const pos = new Map(docs.map((d) => [d, union.map((b) => (d.bands || []).findIndex((x) => x.nominal === b.nominal))]));
  return [...groups.values()].sort((a, b) => String(a.sort).localeCompare(String(b.sort))).map((g) => {
    const w = g.docs.map((d) => d[version].n_blocks || d.seconds || 1);
    const col = (k, i) => g.docs.map((d) => {
      const arr = d[version][k] || [];
      if (i === union.length) return arr[(d.bands || []).length];
      const j = pos.get(d)[i];
      return j >= 0 ? arr[j] : undefined;
    });
    const out = { key: g.key, label: g.label, files: g.docs.length, seconds: w.reduce((a, b) => a + b, 0), leq: [], l50: [], l95: [], l5: [], fileIds: g.docs.map((d) => d.id || d.fileId) };
    for (let i = 0; i < nb; i += 1) {
      out.leq.push(emean(col('leq', i), w));
      out.l50.push(median(col('l50', i)));
      const l95 = col('l95', i).filter(Number.isFinite); const l5 = col('l5', i).filter(Number.isFinite);
      out.l95.push(l95.length ? Math.min(...l95) : null); out.l5.push(l5.length ? Math.max(...l5) : null);
    }
    out.tones = [...new Set(g.docs.flatMap((d) => (d.steadyTones || []).map((t) => t.freqHz)))].sort((a, b) => a - b);
    return out;
  });
}

/** bands flagged in any file of the set */
export function bandFlags(docs) {
  const tone = new Set(); const few = new Set();
  docs.forEach((d) => (d.bands || []).forEach((b) => { if (b.steadyTone) tone.add(b.nominal); if (b.fewBins) few.add(b.nominal); }));
  return { tone, few };
}

/** files of one station that can be compared: same calibration state (and the same band list) */
export function comparable(docs) {
  const cal = docs.filter((d) => d.calibrated); const unc = docs.filter((d) => !d.calibrated);
  return { calibrated: cal, uncalibrated: unc };
}

const q = (v) => (v === null || v === undefined ? '' : (/[",\n]/.test(String(v)) ? `"${String(v).replace(/"/g, '""')}"` : String(v)));
const r1 = (v) => (Number.isFinite(v) ? v.toFixed(1) : '');

/** one row per file: identity, calibration, broadband (all / ambient / incl. equipment), ambient per band, tones */
export function filesCSV(docs, stationName = (id) => id) {
  if (!docs.length) return '';
  const bands = docs[0].bands.map((b) => b.nominal);
  const head = ['station', 'station_id', 'site', 'latitude', 'longitude', 'file', 'recorded_at_utc', 'seconds', 'sample_rate_hz', 'calibrated', 'unit', 'remark',
    'all_broadband_leq', 'ambient_broadband_leq', 'ambient_broadband_l5', 'ambient_broadband_l50', 'ambient_broadband_l95',
    'ambient_incl_equipment_broadband_leq', 'animal_seconds', 'equipment_seconds', 'steady_tones_hz', 'synthetic',
    ...bands.map((b) => `ambient_leq_${b}hz`), ...bands.map((b) => `ambient_l50_${b}hz`)];
  const rows = docs.map((d) => {
    const a = d.ambient || {}; const n = d.bands.length; const L = (s, k, i) => (s && s[k] ? s[k][i] : null);
    return [stationName(d.stationId), d.stationId, d.site?.name || '', d.site?.lat ?? '', d.site?.lon ?? '', d.file, recDate(d).toISOString(), d.seconds, d.sampleRate, d.calibrated ? 'yes' : 'no', d.unit, d.remark || '',
      r1(L(d.all, 'leq', n)), r1(L(a, 'leq', n)), r1(L(a, 'l5', n)), r1(L(a, 'l50', n)), r1(L(a, 'l95', n)), r1(L(d.ambientWithEquipment, 'leq', n)),
      d.animalBlocks, d.equipmentBlocks, (d.steadyTones || []).map((t) => t.freqHz).join(' '), d.synthetic ? 'yes' : '',
      ...d.bands.map((_, i) => r1(L(a, 'leq', i))), ...d.bands.map((_, i) => r1(L(a, 'l50', i)))];
  });
  return [head, ...rows].map((r) => r.map(q).join(',')).join('\n');
}

/** one row per period (what the chart shows) */
export function periodsCSV(rows, bandsList, meta) {
  const bands = bandsList.map((b) => b.nominal);
  const head = ['station', 'period', 'version', 'unit', 'files', 'seconds', 'broadband_leq', 'broadband_median_l50', 'broadband_lowest_l95', 'broadband_highest_l5',
    ...bands.map((b) => `leq_${b}hz`), 'steady_tones_hz'];
  const n = bands.length;
  return [head, ...rows.map((r) => [meta.station, r.label, meta.version, meta.unit, r.files, r.seconds, r1(r.leq[n]), r1(r.l50[n]), r1(r.l95[n]), r1(r.l5[n]),
    ...bands.map((_, i) => r1(r.leq[i])), r.tones.join(' ')])].map((r) => r.map(q).join(',')).join('\n');
}

/** where a recording was made: its site name, else its position, else the station itself */
export const siteOf = (d) => (d.site?.name ? d.site.name : (Number.isFinite(d.site?.lat) && Number.isFinite(d.site?.lon) ? `${d.site.lat.toFixed(3)}, ${d.site.lon.toFixed(3)}` : 'station position'));
